import { isEmpty, map, find, findIndex, reduce } from 'lodash';

import StorefrontApi from 'api/Storefront';
import { CART_MUTATIONS, CART_OPERATIONS, CART_QUERIES } from 'constants/storefront';
import StorefrontCartModel from 'models/StorefrontCart';
import DataDogLogs from 'utils/DataDogLogs';
import DigitalGiftCardUtils from 'utils/DigitalGiftCard';
import Log from 'utils/Log';
import Settings from 'utils/Settings';
import StorefrontUtil from 'utils/Storefront';

import normalizeCart from './schema';

class StorefrontCart {
  static get cartId() {
    const gid = window?.localStorage?.getItem('shopifyCartId') ?? null;
    return gid;
  }

  static set cartId(value) {
    if (!!value && value !== 'undefined') {
      window.localStorage.setItem('shopifyCartId', value);
    }
  }

  static fetchCart = async () => {
    let cart;

    if (StorefrontCart.cartId) {
      try {
        cart = await StorefrontCart._queryCart();
      } catch (error) {
        Log.error(`StorefrontCart:fetchCart - ${error}`);
      }

      if (!cart) {
        DataDogLogs.info(`StorefrontCart:fetchCart - Invalid Cart, init fresh cart`);
        Log.warn(`StorefrontCart:fetchCart - Invalid Cart`);

        cart = await StorefrontCart._initCart();
      }
    } else {
      cart = await StorefrontCart._initCart();
    }

    const abstractedCart = new StorefrontCartModel(cart);
    const normalizedCart = normalizeCart(abstractedCart);
    return normalizedCart;
  };

  static addLineItems = async ({ items = {}, properties = {} }) => {
    const attributes = map(properties, (value, key) => ({
      value,
      key,
    }));

    const lines = map(items, (quantity, id) => {
      const gid = StorefrontUtil.convertToGid({ id, resourceType: 'ProductVariant' });

      return {
        merchandiseId: gid,
        quantity,
        attributes: isEmpty(attributes) ? undefined : attributes,
      };
    });

    let { cart = {}, userErrors = [] } = await StorefrontCart._cartLinesAdd({
      lines,
    });

    if (StorefrontCart._hasValidationError(userErrors)) {
      cart = await StorefrontCart._queryCart();
    }

    const abstractedCart = new StorefrontCartModel(cart);
    const normalizedCart = normalizeCart(abstractedCart);

    return { cart: normalizedCart, userErrors };
  };

  static updateLineItem = async (variantId, quantity, properties = {}) => {
    const attributes = map(properties, (value, key) => ({
      value,
      key,
    }));

    // Process variantId for Digital Gift Cards
    let rawVariantId = variantId;
    if (DigitalGiftCardUtils.isDigitalGiftCardId(variantId)) {
      rawVariantId = DigitalGiftCardUtils.getIdFromDigitalGiftCardId(variantId);
    }

    const gid = StorefrontUtil.convertToGid({
      id: rawVariantId,
      resourceType: 'ProductVariant',
    });
    const { cartLineItem } = await StorefrontCart._getCartLineItemByVariantId({ variantId: gid });

    let { cart = {}, userErrors = [] } = await StorefrontCart._cartLinesUpdate({
      lines: [
        {
          id: cartLineItem?.id,
          quantity,
          attributes: isEmpty(attributes) ? undefined : attributes,
        },
      ],
    });

    if (StorefrontCart._hasValidationError(userErrors)) {
      cart = await StorefrontCart._queryCart();
    }

    const abstractedCart = new StorefrontCartModel(cart);
    const normalizedCart = normalizeCart(abstractedCart);

    return { cart: normalizedCart, errors: userErrors };
  };

  static removeLineItem = variantId => StorefrontCart.updateLineItem(variantId, 0);

  static addDiscounts = async discountCodes => {
    const { cartId } = StorefrontCart;

    const operationName = CART_OPERATIONS.CART_DISCOUNT_CODES_UPDATE;
    const query = CART_MUTATIONS.CART_DISCOUNT_CODES_UPDATE;

    const variables = { cartId, discountCodes };
    const { cart = {}, userErrors = [] } = await StorefrontApi.call({
      query,
      variables,
      operationName,
    });

    StorefrontCart._logUserErrors({ operationName, userErrors });

    const abstractedCart = new StorefrontCartModel(cart);
    const normalizedCart = normalizeCart(abstractedCart);

    return { cart: normalizedCart, errors: userErrors };
  };

  static setAttributes = async newAttributes => {
    const { cartId } = StorefrontCart;

    if (!cartId) {
      throw new Error(`StorefrontCart:setAttribute - Missing cartId (${cartId})`);
    }

    const operationName = CART_OPERATIONS.CART_ATTRIBUTES_UPDATE;
    const query = CART_MUTATIONS.CART_ATTRIBUTES_UPDATE;

    const currentAttributes = await StorefrontCart._getCurrentCartAttributes();
    const currentAttributesJson = reduce(
      currentAttributes,
      (acc, { key, value }) => ({ ...acc, [key]: value }),
      {}
    );
    const mergeAttributes = { ...currentAttributesJson, ...newAttributes };
    const attributes = map(mergeAttributes, (value, key) => ({
      key,
      value,
    }));

    const variables = { cartId, attributes };
    const { cart = {}, userErrors = [] } = await StorefrontApi.call({
      query,
      variables,
      operationName,
    });

    return { cart, userErrors };
  };

  static setAttribute = async (attributeName, value) => {
    const { cartId } = StorefrontCart;

    if (!cartId) {
      throw new Error(`StorefrontCart:setAttribute - Missing cartId (${cartId})`);
    }

    const operationName = CART_OPERATIONS.CART_ATTRIBUTES_UPDATE;
    const query = CART_MUTATIONS.CART_ATTRIBUTES_UPDATE;

    const attributes = await StorefrontCart._getUpdatedAttributes(attributeName, value);

    const variables = { cartId, attributes };
    const { cart = {}, userErrors = [] } = await StorefrontApi.call({
      query,
      variables,
      operationName,
    });

    return { cart, userErrors };
  };

  static _initCart = async () => {
    const cart = await StorefrontCart._createCart();
    StorefrontCart.cartId = cart?.id;

    return cart;
  };

  static _createCart = async () => {
    const operationName = CART_OPERATIONS.CART_CREATE;
    const query = CART_MUTATIONS.CART_CREATE;

    const variables = { attributes: [{ key: 'locale', value: Settings.currentLocale }] };
    const { cart = {} } = await StorefrontApi.call({
      query,
      variables,
      operationName,
    });

    return cart;
  };

  static _queryCart = async () => {
    const { cartId } = StorefrontCart;

    if (!cartId) {
      throw new Error(`StorefrontCart:_queryCart - Missing cartId (${cartId})`);
    }

    const operationName = CART_OPERATIONS.CART_QUERY;
    const query = CART_QUERIES.CART_QUERY;

    const variables = { cartId };
    const cart = await StorefrontApi.call({ query, variables, operationName });
    return cart;
  };

  static _cartLinesAdd = async ({ lines }) => {
    const { cartId } = StorefrontCart;

    if (!cartId) {
      throw new Error(`StorefrontCart:_cartLinesAdd - Missing cartId`);
    }

    const operationName = CART_OPERATIONS.CART_LINES_ADD;
    const query = CART_MUTATIONS.CART_LINES_ADD;

    const variables = {
      cartId,
      lines,
    };

    const { cart = {}, userErrors = [] } = await StorefrontApi.call({
      query,
      variables,
      operationName,
    });

    StorefrontCart._logUserErrors({ operationName, userErrors });

    return { cart, userErrors };
  };

  static _cartLinesUpdate = async ({ lines }) => {
    const { cartId = null } = StorefrontCart;

    if (!cartId) {
      throw new Error(`StorefrontCart:_cartLinesUpdate - Missing cartId`);
    }

    const operationName = CART_OPERATIONS.CART_LINES_UPDATE;

    const query = CART_MUTATIONS.CART_LINES_UPDATE;
    const variables = {
      cartId,
      lines,
    };

    const { cart = {}, userErrors = [] } = await StorefrontApi.call({
      query,
      variables,
      operationName,
    });

    StorefrontCart._logUserErrors({ operationName, userErrors });

    return { cart, userErrors };
  };

  static _getCartLineItemByVariantId = async ({ variantId }) => {
    if (!variantId) {
      throw new Error(`StorefrontCart:_getCartLineItemByVariantId - Missing variantId`);
    }

    const cart = await StorefrontCart._queryCart();

    const cartLineItem = find(cart?.lines.edges, ({ node }) => {
      return node?.merchandise?.id === variantId;
    });

    return {
      cartLineItem: cartLineItem?.node,
    };
  };

  static _getCurrentCartAttributes = async () => {
    const cart = await StorefrontCart._queryCart();
    const attributes = cart?.attributes;
    return attributes;
  };

  static _getUpdatedAttributes = async (attributeName, value) => {
    const attributes = await StorefrontCart._getCurrentCartAttributes();
    const indexOfAttribute = findIndex(attributes, attribute => {
      return attribute.key === attributeName;
    });

    const doesAttributeExist = indexOfAttribute !== -1;

    if (doesAttributeExist) {
      attributes.splice(indexOfAttribute);
    }

    attributes?.push({ key: attributeName, value: `${value}` });
    return attributes;
  };

  static _logUserErrors = ({ operationName, userErrors }) => {
    if (isEmpty(userErrors) || !operationName) {
      return;
    }

    DataDogLogs.error(`StorefrontCart:UserErrors ${operationName} - ${JSON.stringify(userErrors)}`);
  };

  static _hasValidationError(userErrors) {
    return userErrors.find(({ code }) => code === 'VALIDATION_CUSTOM');
  }
}

export default StorefrontCart;
